<template>
    <div class="user-menu" ref="element">
        <div class="opener" @click="openNav">
            <i class="fal fa-bars"></i>
        </div>
        <div v-if="isOpened" class="nav dragable" ref="navElement">
            <UserMenuHeader />

            <div class="inner scrollable">
                <UserMenuItem iconClass="fal fa-hourglass-start" @click="showProduction">
                    {{ trans.navigation.production }}
                </UserMenuItem>
                <UserMenuItem iconClass="fal fa-shopping-cart" @click="showOrders">
                    {{ trans.navigation.orders }}
                </UserMenuItem>
                <UserMenuItem iconClass="fal fa-list" @click="showItems">
                    {{ trans.navigation.items }}
                </UserMenuItem>
                <UserMenuItem iconClass="fal fa-receipt" @click="showOpenBills">
                    {{ trans.navigation.openBills }}
                </UserMenuItem>
                <UserMenuItem iconClass="fal fa-border-style-alt" @click="showRoomPlan">
                    {{ trans.navigation.rooms }}
                </UserMenuItem>
                <UserMenuItem iconClass="fal fa-money-bill-alt" @click="showPayments">
                    {{ trans.navigation.payments }}
                </UserMenuItem>
                <UserMenuItem v-if="hasAccessToReport" iconClass="fal fa-file-spreadsheet" @click="showReports">
                    {{ trans.navigation.reports }}
                </UserMenuItem>
                <UserMenuItem iconClass="fal fa-user-cog" @click="showSettings">
                    {{ trans.navigation.settings }}
                </UserMenuItem>
            </div>
            <UserMenuItem class="signout" iconClass="fal fa-sign-out" :isLoading="isLoggingOut" @click="logout">
                {{ trans.navigation.logout }}
            </UserMenuItem>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { OperatorClient } from "@/operator/OperatorClient";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { trans } from "@/operator/localization/Trans";
    import { DomUtil } from "@/shared/ui/Helper";
    import { Easing, Tween } from "@tweenjs/tween.js";
    import { nextTick, onMounted, ref } from "vue";

    import UserMenuHeader from "@/operator/components/layout/UserMenuHeader.vue";
    import UserMenuItem from "@/operator/components/layout/UserMenuItem.vue";

    // html refs
    const element = ref<HTMLElement | null>(null);
    const navElement = ref<HTMLElement | null>(null);

    const hasAccessToReport = ref<boolean>(false);
    const isOpened = ref(false);
    const isLoggingOut = ref(false);

    const $element = ref<JQuery | undefined>();
    const $nav = ref<JQuery | undefined>();
    const $dim = ref<JQuery | undefined>();

    let runningTween: any;
    let touchCtrl: HammerManager | undefined;
    let isReleasing = false;
    let currentNavPosition: number | undefined;
    let menuWidth = 270;

    // functions
    // #region navigation actions
    function showRoomPlan() {
        closeNav();
        operatorEvents.views.ShowRoomPlan.dispatch({ showSettingsRoomPlan: true });
    }

    function showProduction() {
        closeNav();
        operatorEvents.views.ShowProduction.dispatch();
    }

    function showItems() {
        closeNav();
        operatorEvents.views.ShowItems.dispatch();
    }

    function showOpenBills() {
        closeNav();
        operatorEvents.views.ShowOpenBills.dispatch();
    }

    function showOrders() {
        closeNav();
        operatorEvents.views.ShowOrders.dispatch();
    }

    function showSettings() {
        closeNav();
        operatorEvents.views.ShowSettings.dispatch();
    }

    function showReports() {
        closeNav();
        operatorEvents.views.ShowReports.dispatch();
    }

    function showPayments() {
        closeNav();
        operatorEvents.views.ShowPayments.dispatch();
    }

    async function logout() {
        isLoggingOut.value = true;
        try {
            await OperatorClient.context.signOut();
            closeNav();
        } finally {
            isLoggingOut.value = false;
        }
    }
    //#endregion

    async function openNav() {
        if (isOpened.value) return;

        isOpened.value = true;

        await nextTick(); // wait next tick for html to render

        $nav.value = $(navElement.value as any);
        if ($nav.value === undefined) throw new Error("nav element not found");

        touchCtrl = new Hammer($nav.value[0], {
            recognizers: [[Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0 }]],

            domEvents: false,
        });

        touchCtrl.on("panstart", () => {
            if (runningTween !== undefined) {
                runningTween.stop();
                runningTween = undefined;
            }

            isReleasing = false;
            currentNavPosition = DomUtil.translatePosition($nav.value!).x;
        });

        touchCtrl.on("panmove", (ev) => {
            moveNav(ev.deltaX);
        });

        touchCtrl.on("panend", () => {
            if (isReleasing) return;

            isReleasing = true;
            releaseNav();
        });

        if ($dim.value === undefined) {
            const parentView = $element.value!.parents(".view");
            $dim.value = $('<div class="navdim" />')
                .prependTo(parentView)
                .click((e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeNav();
                });
        }

        tweenTo(false, true);
    }

    function closeNav() {
        if (!isOpened.value) return;

        tweenTo(true, false);
    }

    function onNavClosed() {
        $dim.value!.remove();
        $dim.value = undefined;

        $nav.value = undefined;
        touchCtrl!.destroy();
        touchCtrl = undefined;
        isOpened.value = false;
    }

    function moveNav(byPixel: number) {
        let newPosition = currentNavPosition! + byPixel;
        let overdraging = 0;
        if (newPosition < 0) overdraging = newPosition;

        let p = 1 - (1 / menuWidth) * newPosition;
        if (p > 1) p = 1;
        else if (p < 0) p = 0;

        newPosition += DomUtil.momentumResistance(overdraging, 40);
        $dim.value!.css("opacity", p);
        $nav.value!.css({ transform: "translate3d(" + newPosition + "px, 0, 0)" });
    }

    function releaseNav() {
        const currentPos = DomUtil.translatePosition($nav.value!).x;
        tweenTo(currentPos > 50, false);
    }

    function tweenTo(hidden: boolean, useBounce: boolean) {
        const current = {
            pos: DomUtil.translatePosition($nav.value!).x,
            opacity: $dim.value!.css("opacity"),
        };

        const dest = {
            pos: hidden ? menuWidth : 0,
            opacity: hidden ? 0 : 1,
        };

        runningTween = new Tween(current)
            .to(dest, 300)
            .easing(useBounce ? Easing.Back.Out : Easing.Exponential.Out)
            .onUpdate((f) => {
                $dim.value!.css("opacity", f.opacity);
                $nav.value!.css({ transform: "translate3d(" + f.pos + "px, 0, 0)" });
            })
            .start();

        runningTween.onComplete(() => {
            if (hidden) {
                onNavClosed();
            }

            isReleasing = false;
            runningTween = undefined;
        });

        const animate = (time?: null) => {
            if (runningTween !== undefined && runningTween.isPlaying()) {
                runningTween.update(time);
                requestAnimationFrame(animate as any);
            }
        };

        animate();
    }

    onMounted(() => {
        $element.value = $(element.value as any);
        hasAccessToReport.value = getHasAccessToReport();

        operatorEvents.context.authenticationChanged.add((isAuthenticated: boolean) => {
            //reset on logout
            if (!isAuthenticated) {
                hasAccessToReport.value = false;
            } else {
                hasAccessToReport.value = getHasAccessToReport();
            }
        });

        operatorEvents.context.loaded.add(() => {
            hasAccessToReport.value = getHasAccessToReport();
        });
    });

    function getHasAccessToReport() {
        return OperatorClient.context?.profile?.hasAccessToNonPortalReports ?? false;
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_variables.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .user-menu {
        position: absolute;
        right: 0;
        top: 0;
        display: block;
        cursor: pointer;

        > .opener {
            width: 38px;
            height: 34px;
            background-color: #a6d234;
            margin-right: 16px;
            margin-top: 9px;
            text-align: center;
            pointer-events: all;
            cursor: pointer;

            > .fal {
                color: #fff;
                font-size: 16px;
                line-height: 34px;
                display: inline-block;
                position: relative;
            }
        }

        > .nav {
            background-color: #ffffff;
            position: fixed;
            top: 0;
            bottom: 0;
            right: -50px;
            z-index: 600;
            width: 320px;
            -webkit-box-shadow: 10px 10px 57px 0px rgba(72, 72, 72, 0.11);
            -moz-box-shadow: 10px 10px 57px 0px rgba(72, 72, 72, 0.11);
            box-shadow: 10px 10px 57px 0px rgba(72, 72, 72, 0.11);
            transform: translate3d(270px, 0, 0);
            pointer-events: all;

            > .inner {
                position: absolute;
                top: 117px;
                left: 0;
                right: 50px;
                bottom: 72px;
                padding: 16px 0;
            }

            .signout {
                position: absolute;
                bottom: 26px;
                left: 0;
                right: 0;
            }
        }
    }
</style>
